import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { COMPANY } from "../lib/data";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/services", label: "Services" },
  { to: "/projects", label: "Projects" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-white/85 backdrop-blur-xl border-b border-[#E2E8F0]"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 flex items-center justify-between h-20">
        <Link to="/" data-testid="brand-logo" className="flex items-center gap-3 group">
          <img src="/logo.png" alt="Alex Interiors Logo" className="h-10 w-auto object-contain" />
          <div className="flex flex-col">
            <span className="font-heading text-3xl font-bold tracking-tight text-[#0A192F] leading-none">
              Alex<span className="text-[#C5A059]"> </span><span className="text-[#0A192F]">Interiors</span>
            </span>
            <span className="text-[10px] tracking-[0.2em] font-semibold text-[#C5A059] uppercase mt-1">
              {COMPANY.tagline}
            </span>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-10">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`nav-link-${n.label.toLowerCase()}`}
              className={({ isActive }) =>
                `text-sm font-medium tracking-wide transition-colors duration-300 ${
                  isActive ? "text-[#C5A059]" : "text-[#0A192F] hover:text-[#C5A059]"
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:block">
          <Link
            to="/contact"
            data-testid="nav-contact-cta"
            className="inline-flex items-center gap-2 bg-[#0A192F] text-white px-6 py-3 text-sm font-medium tracking-wide hover:bg-[#C5A059] transition-colors duration-300"
          >
            Get in Touch →
          </Link>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          aria-label="Toggle menu"
          onClick={() => setOpen((s) => !s)}
          className="lg:hidden p-2 text-[#0A192F]"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="lg:hidden bg-white border-t border-[#E2E8F0]">
          <div className="px-6 py-6 flex flex-col gap-5">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`mnav-link-${n.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `text-base font-medium ${isActive ? "text-[#C5A059]" : "text-[#0A192F]"}`
                }
              >
                {n.label}
              </NavLink>
            ))}
            <Link
              to="/contact"
              data-testid="mnav-contact-cta"
              className="mt-2 inline-flex items-center justify-center bg-[#0A192F] text-white px-6 py-3 text-sm font-medium"
            >
              Get in Touch →
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
